import type { GridColDef } from '@mui/x-data-grid';
import { formatRevSharePrice, type PerUnitProduct } from '@vy/services';
import { COMMITMENT_TYPES, type Currency, type FixedProduct, type UsageProduct } from '@vy/types';
import { formatNumber, isNotNullOrUndefined, isNullOrUndefined, withDefault } from '@vy/utils';
import { FC } from 'react';
import { Price } from '../../../../../components/design/Price';
import { VyTableCell } from '../../../../../components/new-design/table/vy-table/VyTableCell';
import { getAccumulationIntervalLabel } from '../../../../plans/CreatePlanPage/shared-charge-drawer-components/components/AccumulationIntervalSelect';
import { getBillingIntervalLabel } from '../../ContractSummaryDetails/contract-summary-details-utils/get-billing-interval-label';
import { ContractTable } from '../../shared/ContractTable';
import { AnrokCell } from '../../shared/AnrokCell';
import type { OnEditAnrokHandler } from '../../shared/anrok-cell-types';
import { ErpCell } from '../../shared/ErpCell';
import { isProduction } from '../../../../../utils/env-indicator';
import type { UsageProductsColumns } from '../contract-usage-products-types/usage-products-columns';
import { USAGE_PRODUCTS_COLUMNS_KEYS } from '../contract-usage-products-constants/UsageProductsColumns';

type ContractUsageProductsTableProps = {
  rows: UsageProductsColumns[];
  columns: GridColDef<UsageProductsColumns>[];
  currency: Currency;
  onEditErp?: (product: FixedProduct | PerUnitProduct | UsageProduct, productName: string) => void;
  onEditAnrok?: OnEditAnrokHandler;
};

export const ContractUsageProductsTable: FC<ContractUsageProductsTableProps> = ({
  rows,
  columns,
  currency,
  onEditErp,
  onEditAnrok,
}) => {
  return (
    <ContractTable
      rows={rows}
      columns={columns}
      currency={currency}
      renderCell={(row, column) => renderCellContent(row, column, currency, onEditErp, onEditAnrok)}
      hasExpandableRows={true}
      firstColumnField="productName"
    />
  );
};

function renderCellContent(
  row: UsageProductsColumns,
  column: GridColDef<UsageProductsColumns>,
  currency: Currency,
  onEditErp?: (product: FixedProduct | PerUnitProduct | UsageProduct, productName: string) => void,
  onEditAnrok?: OnEditAnrokHandler,
): React.ReactNode {
  switch (column.field) {
    case USAGE_PRODUCTS_COLUMNS_KEYS.PRODUCT_NAME:
      return <VyTableCell value={row.productName} containerClassName="justify-start" />;

    case USAGE_PRODUCTS_COLUMNS_KEYS.PRICING_TYPE:
      return <VyTableCell value={row.pricingType} containerClassName="justify-start" />;

    case USAGE_PRODUCTS_COLUMNS_KEYS.PRICE:
      if (isNullOrUndefined(row.price)) {
        return <VyTableCell value="--" />;
      }
      if (row.isRevShare) {
        const formattedPrice = formatRevSharePrice(row.price);
        return (
          <VyTableCell
            value={row.isParent ? '--' : formattedPrice}
            containerClassName={row.isParent ? undefined : 'justify-start'}
          />
        );
      }
      return (
        <VyTableCell value={<Price currency={currency}>{row.price}</Price>} containerClassName="justify-start" />
      );

    case USAGE_PRODUCTS_COLUMNS_KEYS.RATE:
      return (
        <VyTableCell value={<Price currency={currency}>{row.rate ?? 0}</Price>} containerClassName="justify-start" />
      );

    case USAGE_PRODUCTS_COLUMNS_KEYS.PRODUCT_ERP_ID:
      return <ErpCell value={row.productErpId} product={row.product} productName={row.productName} onEditErp={onEditErp} disableEdit={!!row.isChild} />;

    case USAGE_PRODUCTS_COLUMNS_KEYS.MARKETPLACE_PRODUCT_ID:
      return <VyTableCell value={row.marketplaceProductId} containerClassName="justify-start" />;

    case USAGE_PRODUCTS_COLUMNS_KEYS.MARKETPLACE_DIMENSION:
      return <VyTableCell value={row.marketplaceDimension} containerClassName="justify-start" />;

    case USAGE_PRODUCTS_COLUMNS_KEYS.PRODUCT_ANROK_ID: {
      const isInherited = isNullOrUndefined(row.productAnrokOverrideId) && isNotNullOrUndefined(row.productAnrokCatalogDefaultId);
      return (
        <AnrokCell
          value={row.productAnrokId}
          product={row.product}
          productName={row.productName}
          isInherited={isInherited}
          catalogAnrokId={row.productAnrokCatalogDefaultId}
          overrideAnrokId={row.productAnrokOverrideId}
          onEditAnrok={onEditAnrok}
          disableEdit={!!row.isChild}
        />
      );
    }

    case USAGE_PRODUCTS_COLUMNS_KEYS.COMMITMENT:
      if (isNullOrUndefined(row.commitment)) {
        return <VyTableCell value="--" />;
      }
      return (
        <VyTableCell
          value={`${formatNumber(row.commitment)} ${row.commitmentType === COMMITMENT_TYPES.Price ? '$' : withDefault(row.unitsType, 'units')}`}
          containerClassName="justify-start"
        />
      );

    case USAGE_PRODUCTS_COLUMNS_KEYS.METER_RESET: {
      const meterResetValue = isNotNullOrUndefined(row.meterReset)
        ? getAccumulationIntervalLabel(row.meterReset)
        : '--';
      return (
        <VyTableCell
          value={meterResetValue}
          containerClassName={meterResetValue !== '--' ? 'justify-start' : undefined}
        />
      );
    }

    case USAGE_PRODUCTS_COLUMNS_KEYS.METER_REFILL: {
      const meterRefillValue = isNotNullOrUndefined(row.meterRefill)
        ? getAccumulationIntervalLabel(row.meterRefill)
        : '--';
      return (
        <VyTableCell
          value={meterRefillValue}
          containerClassName={meterRefillValue !== '--' ? 'justify-start' : undefined}
        />
      );
    }

    case USAGE_PRODUCTS_COLUMNS_KEYS.BULK:
      if (isNullOrUndefined(row.bulk) || row.bulk === 1) {
        return <VyTableCell value="--" />;
      }
      return <VyTableCell value={formatNumber(row.bulk)} containerClassName="justify-start" />;

    case USAGE_PRODUCTS_COLUMNS_KEYS.BILLING_INTERVAL: {
      const billingIntervalValue = isNotNullOrUndefined(row.billingInterval)
        ? getBillingIntervalLabel(row.billingInterval)
        : '--';
      return (
        <VyTableCell
          value={billingIntervalValue}
          containerClassName={billingIntervalValue !== '--' ? 'justify-start' : undefined}
        />
      );
    }

    case USAGE_PRODUCTS_COLUMNS_KEYS.START_DATE:
      return (
        <VyTableCell value={row.startDate} containerClassName={row.startDate ? 'justify-start' : undefined} />
      );

    default:
      if (!isProduction()) {
        console.warn(`[ContractUsageProductsTable] No cell mapping for column field: "${column.field}". Add a case to renderCellContent.`);
      }
      return '--';
  }
}

